import dayjs, { Dayjs } from 'dayjs';

import { storageService } from '@/shared/storage/export';
import { Observation } from '@/modules/calendar/model/calendar/observation';
import { authService } from '@/core/auth/export';
import {
  analyticsService,
  AnalyticsEvent,
  AnalyticsSubTypeEvent,
  AnalyticsTypes,
} from '@/shared/analytics/export';
import { messageService, MessageServiceConstants } from '@/core/messaging/export';
import { DataSeriesReading } from '@/modules/calendar/services/data-series-reading';
import { NewsEvent } from '@/modules/calendar/model/calendar/news-event';
import { Article } from '@/modules/articles/export';

let dataSeriesReading: DataSeriesReading | undefined;
let includeSections: string[] = [];

let scheduledTask: any;

const READING_STORAGE_KEY = 'scheduled_reading';
const SECTIONS_STORAGE_KEY = 'reader_sections';

//pull up saved reading if it exists
storageService.getValue<DataSeriesReading>(READING_STORAGE_KEY).then(value => {
  console.log('Loading scheduled reading');

  if (value && dayjs(value.date).isAfter(dayjs())) scheduleReading(value);
});

//pull up saved sections if they exist
storageService.getValue<string[]>(SECTIONS_STORAGE_KEY).then(value => {
  console.log('Loading reader sections');

  if (value) {
    includeSections = value;
  }
});

messageService.subscribe<Observation[]>(MessageServiceConstants.SELECT_TOPIC, observations => {
  observations.forEach(observation => {
    if (observation.dataSeriesId === dataSeriesReading?.dataSeriesId) {
      console.log('Reading data series', observation.dataSeriesId);
      readText(`${observation.value}`);
      setScheduledReading();
    }
  });
});

messageService.subscribe<Article>(
  MessageServiceConstants.NEWS_ARTICLE_TOPIC,
  (message, headers) => {
    const sections: string[] = JSON.parse(headers.sections);
    console.log('Received news articles:', sections, message.headline, includeSections);

    if (sections.some(value => includeSections.includes(value))) {
      console.log('Reading article', message.headline);
      readText(`${message.body_text}`);
    }
  },
);

function readText(text: string) {
  text = text.toLowerCase().replaceAll(/m\/m/g, ', month-on-month');
  text = text.toLowerCase().replaceAll(/y\/y/g, ', year-on-year');
  text = text.toLowerCase().replaceAll(/w\/w/g, ', week-on-week');
  text = text.toLowerCase().replaceAll(/ex\./g, 'excluding');

  window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
}

function readScheduledHeadingOr(text: string) {
  if (dataSeriesReading) readText(dataSeriesReading?.text || '');
  else readText(text);
}

function setScheduledReading(r?: DataSeriesReading) {
  dataSeriesReading = r;
  storageService.saveValue(READING_STORAGE_KEY, dataSeriesReading);

  if (dataSeriesReading) {
    dataSeriesReading.date = new Date(dataSeriesReading.date);
  }
}

function scheduleReading(
  dataSeriesReading?: DataSeriesReading,
  event?: NewsEvent,
  sessionId?: string,
) {
  clearTimeout(scheduledTask);

  setScheduledReading(dataSeriesReading);

  if (!dataSeriesReading) return;

  const subject = authService.getSubjectData();

  if (event) {
    analyticsService.recordAnalyticsEvent(AnalyticsEvent.HSC_READER_SCHEDULED, {
      subject,
      object: {
        type: AnalyticsTypes.EVENT,
        eventSubType: AnalyticsSubTypeEvent.DATA_EVENT,
        id: event.id,
        title: `[${event.country}] ${event.title}`,
        config: {
          date: event.date,
          dataReleaseId: event.dataReleaseId,
          dataSeriesIds: event.dataSeriesEntries.map(ds => ds.dataSeriesId),
          instantAnswers: event.instantAnswers?.map(ia => ({
            instantAnswersSeriesId: ia.instantAnswersSeriesId,
            instantAnswersInstanceId: ia.instantAnswersInstanceId,
            questionIds: ia.questions.map(q => q.questionId),
          })),
          reader: {
            title: dataSeriesReading.text,
            dataSeriesId: dataSeriesReading.dataSeriesId,
          },
        },
      },
      session: {
        id: sessionId,
      },
    });
  }

  const eventTime = dataSeriesReading.date.getTime();
  const leadTime = eventTime - new Date().getTime() - 5000;

  scheduledTask = setTimeout(() => {
    readText(dataSeriesReading?.text || '');
  }, leadTime);

  console.log(
    'Scheduled reading',
    dataSeriesReading,
    dataSeriesReading.date,
    dayjs.tz(eventTime).tz(),
  );

  console.log('Reading announcement');

  readText(`Will read '${dataSeriesReading.text}' at ${dayjs.tz(eventTime).format('hh:mm a')}`);
}

function getDataSeriesReading(): DataSeriesReading | undefined {
  if (dataSeriesReading) {
    //if in future get it, else clear the reader
    if (dataSeriesReading.date.getTime() > new Date().getTime() - 30_000) return dataSeriesReading;
    else scheduleReading();
  }
}

function isReading(dataSeriesId: number, date: Date | string | number | Dayjs): boolean {
  if (!dataSeriesReading) return false;

  date = dayjs(date).toDate();

  return (
    dataSeriesReading.dataSeriesId === dataSeriesId &&
    dataSeriesReading.date.getTime() === date.getTime()
  );
}

function setReaderSections(sections: string[]) {
  includeSections = sections;
  storageService.saveValue(SECTIONS_STORAGE_KEY, includeSections);
}

function getReaderSections(): string[] {
  return includeSections;
}

// eslint-disable-next-line
export default {
  getDataSeriesReading,
  isReading,
  readScheduledHeadingOr,
  scheduleReading,
  setReaderSections,
  getReaderSections,
};
